
import {
  defineComponent, computed, reactive, onBeforeMount, inject, Ref, watch,
} from 'vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import Icone from '@/core/components/Icone.vue';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import { IListaPaginadaMetaData } from '@/core/models/Consulta/IListaPaginada';
import { useGradeBase } from '@/core/composables/GradeBase';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import { IPagination } from '@/core/models/AntDesign/IPagination';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { IDTOVenda } from '@/models/DTO/Vendas/IDTOVenda';
import { IProcessoVendas } from '@/models/Entidades/Cadastros/Vendas/IProcessoVendas';
import storeSistema from '@/store/storeSistema';
import { IDTOOrdemProducao } from '@/models/DTO/PCP/IDTOOrdemProducao';
import { EStatusProducao } from '@/models/Enumeradores/PCP/EStatusProducao';

export default defineComponent({
  name: 'ProcessoProducaoListagem',
  components: {
    Icone,
    MensagemSemDados,
  },
  props: {
    visualizacaoKanban: {
      type: Boolean,
      default: true,
    },
    ops: {
      type: Array as () => IDTOOrdemProducao[],
      required: true,
    },
    selecionados: {
      type: Array as () => number[],
      required: true,
    },
    paginacao: {
      type: Object as () => IPagination,
      required: true,
    },
  },
  emits: ['update:ops', 'update:visualizacaoKanban', 'update:paginacao', 'update:selecionados', 'selecaoAlterada', 'buscarOps', 'visualizar', 'imprimir', 'novoLancamento', 'excluir'],
  setup(props, { emit }) {
    const { telaBase } = useTelaBase();
    const {
      gradeBase, preencheOrdenacaoSelecionada, ordenacaoAtiva, defineAlturaScroll,
    } = useGradeBase();

    const state = reactive({
      colunas: [] as IColumn[],
      paginacaoApi: { totalRegistros: 0, paginaAtual: 1 } as IListaPaginadaMetaData,
      timerRegistros: 0,
      totalRegistrosComMascara: '100',
      telaMobile: false,
    });

    const computedOps = computed({
      get: () => props.ops,
      set: (valor: IDTOOrdemProducao[]) => {
        emit('update:ops', valor);
      },
    });

    const computedVisualizacaoKanban = computed({
      get: () => props.visualizacaoKanban,
      set: (valor: boolean) => {
        emit('update:visualizacaoKanban', valor);
      },
    });

    const computedSelecionados = computed({
      get: () => props.selecionados,
      set: (valor: number[]) => {
        emit('update:selecionados', valor);
      },
    });

    const computedPaginacao = computed({
      get: () => props.paginacao,
      set: (valor: IPagination) => {
        emit('update:paginacao', valor);
      },
    });

    function preencherColunas() {
      gradeBase.colunasMobile = [
        {
          title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false,
        },
        {
          title: 'Produto', dataIndex: 'produto', key: 'Descricao', position: 2, visible: true,
        },
      ];

      gradeBase.colunasPadrao = [
        {
          title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false,
        },
        {
          title: 'Número', dataIndex: 'numeroInterno', key: 'NumeroInterno', position: 2, visible: true, width: 50, ellipsis: true, customRenderRow: ECustomRenderRow.LinkAcao, align: 'left',
        },
        {
          title: 'Produto', dataIndex: 'descricao', key: 'Descricao', position: 2, visible: true, width: 100, ellipsis: true, customRenderRow: ECustomRenderRow.LinkAcao, align: 'left',
        },
        {
          title: 'Tipo de Produção', dataIndex: 'tipoProducao', key: 'TipoProducao', position: 3, visible: true, ellipsis: true, customRenderRow: ECustomRenderRow.LinkAcao, width: 50,
        },
        {
          title: 'Status', dataIndex: 'descricaoStatus', key: 'Status', position: 4, visible: true, ellipsis: true, width: 50,
        },
        {
          title: 'Quantidade', dataIndex: 'quantidade', key: 'Quantidade', position: 5, visible: true, customRenderRow: ECustomRenderRow.MascararFracionado, align: 'center', width: 50,
        },
        {
          title: 'Numero do Lote', dataIndex: 'numeroLote', key: 'NumeroLote', position: 5, visible: true, align: 'left', width: 50,
        },
        {
          title: 'Qtd. Produzida', dataIndex: 'qtdProduzidaFinal', key: 'QuantidadeProduziada', position: 5, visible: true, customRenderRow: ECustomRenderRow.MascararFracionado, align: 'center', width: 50,
        },
        {
          title: 'Data de Criação', dataIndex: 'dataCriacao', key: 'DataCriacao', position: 6, visible: true, customRenderRow: ECustomRenderRow.MascararData, ordering: false, width: 70, align: 'center',
        },
        {
          title: 'Data de Inicio', dataIndex: 'dataInicio', key: 'DataInicio', position: 7, visible: true, customRenderRow: ECustomRenderRow.MascararData, ordering: false, width: 70, align: 'center',
        },
        {
          title: 'Data de Conclusão', dataIndex: 'dataConclusao', key: 'DataConclusao', position: 8, visible: true, customRenderRow: ECustomRenderRow.MascararData, ordering: false, width: 70, align: 'center',
        },
        {
          title: 'Ações', key: 'acoes', position: 9, visible: true, width: 75, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    async function ordenarDados(key: string, sort: string) {
      await preencheOrdenacaoSelecionada(key, sort);
      emit('buscarOps', gradeBase.ordenacaoSelecionada);
    }

    async function alterarQuantidadeRegistroTotal(valor: any) {
      clearTimeout(state.timerRegistros);
      let totalRegistros = 100;
      state.timerRegistros = setTimeout(async () => {
        if (UtilitarioGeral.valorValido(valor)) {
          const limiteRegistros = UtilitarioMascara.removerMascaraNumero(valor);
          if (limiteRegistros > 0) {
            totalRegistros = limiteRegistros;
          }
        }
        computedPaginacao.value.total = totalRegistros;
        state.totalRegistrosComMascara = UtilitarioMascara.mascararNumeroInteiro(totalRegistros);
        emit('buscarOps');
      }, 600);
    }

    async function mudarPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = current;
      computedPaginacao.value.pageSize = pageSize;
      emit('buscarOps');
    }

    async function mostrarNovaPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = 1;
      computedPaginacao.value.pageSize = pageSize;
      emit('buscarOps');
    }

    const mudarSelecao = (selectedRowKeys: any) => {
      computedSelecionados.value = selectedRowKeys;
      emit('selecaoAlterada');
    };

    function obterStatus(status: EStatusProducao) {
      switch (status) {
        case EStatusProducao.Pendente:
          return 'pendente';
        case EStatusProducao.Produzindo:
          return 'produzindo';
        case EStatusProducao.Concluido:
          return 'concluido';
        case EStatusProducao.Cancelado:
          return 'cancelado';
        default:
          return 'interrompido';
      }
    }

    function visualizar(index: number) {
      emit('visualizar', props.ops[index].codigo);
    }

    function excluir(index: number) {
      emit('excluir', props.ops[index].codigo);
    }

    function imprimir(index: number) {
      emit('imprimir', props.ops[index].codigo);
    }

    function novoLancamento() {
      emit('novoLancamento');
    }

    onBeforeMount(async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      preencherColunas();
      computedPaginacao.value.total = 100;
      computedPaginacao.value.showSizeChanger = true;
      computedPaginacao.value.pageSize = 100;
      computedPaginacao.value.pageSizeOptions = ['10', '50', '100', '500', '1000'];
    });

    watch(() => storeSistema.state.layoutMobile, () => {
      preencherColunas();
    });

    return {
      state,
      storeSistema,
      props,
      window,
      telaBase,
      gradeBase,
      computedOps,
      computedVisualizacaoKanban,
      computedSelecionados,
      computedPaginacao,
      obterStatus,
      UtilitarioMascara,
      UtilitarioData,
      mudarPagina,
      ordenarDados,
      mostrarNovaPagina,
      alterarQuantidadeRegistroTotal,
      mudarSelecao,
      ordenacaoAtiva,
      ECustomRenderRow,
      visualizar,
      imprimir,
      excluir,
      novoLancamento,
      defineAlturaScroll,
    };
  },
});

